import { useState, useEffect, useCallback } from 'react'
import { listCards, createCard, updateCard, deleteCard } from '../services/cardService'
import type { Card } from '../types'

export function useCards(householdId: string | null) {
  const [cards, setCards]   = useState<Card[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError]   = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!householdId) return
    setLoading(true)
    setError(null)
    try {
      setCards(await listCards(householdId))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [householdId])

  useEffect(() => { fetch() }, [fetch])

  async function addCard(card: Omit<Card, 'id'>): Promise<Card> {
    if (!householdId) throw new Error('No household')
    const created = await createCard(householdId, card)
    await fetch()
    return created
  }

  async function editCard(id: string, changes: Partial<Omit<Card, 'id'>>): Promise<void> {
    await updateCard(id, changes)
    setCards(prev => prev.map(c => c.id === id ? { ...c, ...changes } : c))
  }

  async function removeCard(id: string): Promise<void> {
    await deleteCard(id)
    setCards(prev => prev.filter(c => c.id !== id))
  }

  return { cards, loading, error, addCard, editCard, removeCard }
}
